
  import { createPopper } from '@popperjs/core'
  import { getOrCreateRootOverlayHost, OVERLAY_HOST_SELECTOR } from '../dialog-stack.js'
  import { findLayeredPopupHost, setOverlayOwner } from '../overlay-host.js'
  import { findAncestorWithClass } from '../utils/find-ancestor'
  import { loadStyle } from '../utils/css'

  const MANUAL_TRIGGER = 'manual'
  const TARGET_EVENT_NAMES = ['click', 'mouseenter', 'mouseleave', 'focus', 'blur']
  const DEFAULT_POINT_OFFSET_X = 0
  const DEFAULT_POINT_OFFSET_Y = 0
  const DEFAULT_EXCLUSIVE_GROUP = 'default'
  const POPPER_VIEWPORT_PADDING = 8
  const POPPER_VIEWPORT_INSET = `${POPPER_VIEWPORT_PADDING * 2}px`
  const activeExclusivePopups = new Map<string, any[]>()
  const STYLE = `
    .ae-popup__body {
      display: contents;
    }

    .ae-popup--mobile {
      position: fixed !important;
      inset: 50% auto auto 50% !important;
      display: flex;
      flex-direction: column;
      width: max-content;
      max-width: calc(100vw - 16px) !important;
      max-height: calc(100vh - 16px);
      transform: translate(-50%, -50%) !important;
      border-radius: var(--ae-popup-mobile-border-radius, var(--ae-border-radius-lg, 0.75rem));
      background: var(--ae-popup-mobile-background, var(--ae-ui-background, #fff));
      color: var(--ae-popup-mobile-color, var(--ae-foreground, #111827));
      box-shadow: var(--ae-popup-mobile-shadow, 0 18px 48px rgba(15, 23, 42, 0.28));
      overflow: hidden;
    }

    .ae-popup--mobile-fullscreen {
      inset: 8px !important;
      width: auto;
      max-width: none !important;
      max-height: none;
      transform: none !important;
    }

    .ae-popup--mobile .ae-popup__header {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--ae-space-2, 0.5rem);
      min-height: 48px;
      padding: 0 var(--ae-space-3, 0.75rem) 0 var(--ae-space-4, 1rem);
      border-bottom: var(--ae-border-width, 1px) solid var(--ae-border-color, #d8dde6);
      background: var(--ae-popup-mobile-header-background, var(--ae-ui-background, #fff));
    }

    .ae-popup--mobile .ae-popup__title {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 1rem;
      font-weight: 700;
      line-height: 1.2;
    }

    .ae-popup--mobile .ae-popup__close {
      flex: 0 0 auto;
      width: 36px;
      height: 36px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }

    .ae-popup--mobile .ae-popup__body {
      display: block;
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      padding: var(--ae-popup-mobile-padding, var(--ae-space-4, 1rem));
    }

    .ae-popup--mobile .ae-popup__body > * {
      max-width: 100%;
      box-sizing: border-box;
    }
  `

  function resolveDelay(delay, index) {
    if (Array.isArray(delay)) {
      return Number(delay[index]) || 0
    }

    return Number(delay) || 0
  }

  function normalizeTarget(target: HTMLElement|string|any) {
    if (typeof target !== 'string') {
      return target
    }

    if (target.startsWith('ae_i')) {
      target = `#${target}` // Assume it's an ID selector
    }

    return document.querySelector(target) as HTMLElement
  }

  function isNode(value: any) {
    return !!(value && typeof value === 'object' && typeof value.nodeType === 'number')
  }

  function elementContains(target: any, element: EventTarget|null|any) {
    return !!(target && typeof target.contains === 'function' && isNode(element) && target.contains(element))
  }

  function hasClosest(value: unknown): value is HTMLElement {
    return !!(value && typeof value === 'object' && typeof (value as HTMLElement).closest === 'function')
  }

  function targetIsInsidePopup(popupComponent: any, target: any) {
    const popup = popupComponent?.$refs?.popup
    return elementContains(popup, target) || elementContains(popup, target?.contextElement)
  }

  function getNearestTargetOverlayHost(target: HTMLElement | string | { contextElement?: unknown } | null | undefined) {
    let targetElement: unknown = null

    if (isNode(target)) {
      targetElement = target
    } else if (target && typeof target === 'object' && 'contextElement' in target) {
      targetElement = target.contextElement
    }

    const nearestHost = hasClosest(targetElement) ? targetElement.closest(OVERLAY_HOST_SELECTOR) as HTMLElement | null : null

    // Popups inside docked/application dialogs must escape their bounded host.
    // Reuse only viewport-wide hosts; otherwise fall back to the root host.
    return nearestHost?.dataset.aeOverlayBounded === 'true' ? null : nearestHost
  }

  function hasPointCoordinates(value: any) {
    return value
      && Number.isFinite(Number(value.clientX))
      && Number.isFinite(Number(value.clientY))
  }

  function createPointTarget(input: any, fallbackTarget: any) {
    const baseTarget = normalizeTarget(input.target || fallbackTarget)
    const ownerDocument = baseTarget?.ownerDocument || document
    const x = Number(input.clientX) + Number(input.offsetX ?? input.pointOffsetX ?? DEFAULT_POINT_OFFSET_X)
    const y = Number(input.clientY) + Number(input.offsetY ?? input.pointOffsetY ?? DEFAULT_POINT_OFFSET_Y)

    return {
      ownerDocument,
      contextElement: baseTarget || ownerDocument.documentElement,
      contains: () => false,
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: y,
        right: x,
        bottom: y,
        left: x,
        x,
        y,
      }),
    }
  }

  function normalizePopupMaxWidth(maxWidth: string|number|null|undefined) {
    if (maxWidth === '') {
      return null
    }

    if (typeof maxWidth === 'number') {
      return Number.isFinite(maxWidth)
        ? `min(${maxWidth}px, calc(100vw - ${POPPER_VIEWPORT_INSET}))`
        : null
    }

    if (typeof maxWidth === 'string') {
      const value = maxWidth.trim()

      if (!value || value === 'none') {
        return value || null
      }

      return `min(${value}, calc(100vw - ${POPPER_VIEWPORT_INSET}))`
    }

    return null
  }

  export default {
  /* tslint:disable */
  render () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('span',{staticClass:"ae-popup-host"},[_vm._t("base",null,{"open":_vm.open,"show":_vm.show,"close":_vm.close,"toggle":_vm.toggle,"position":_vm.position,"closeOnBlur":_vm.closeOnBlur,"isOpen":_vm.isOpen,"data":_vm.data,"baseWidth":_vm.baseWidth}),_vm._v(" "),_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.visible),expression:"visible"}],ref:"popup",staticClass:"ae-popup",class:_vm.popupClasses,style:(_vm.style),attrs:{"data-ae-popup-open":_vm.visible ? 'true' : 'false',"data-ae-popup-kind":_vm.kind,"data-ae-popup-mobile":_vm.mobilePresentation ? 'true' : 'false',"data-placement":_vm.placement,"role":_vm.popupRole,"aria-modal":_vm.mobilePresentation && _vm.popupRole === 'dialog' ? 'true' : null},on:{"mouseenter":_vm.popupMouseEnter,"mouseleave":_vm.popupMouseLeave}},[(_vm.mobilePresentation)?_c('div',{staticClass:"ae-popup__header"},[_c('div',{staticClass:"ae-popup__title"},[_vm._t("title",function(){return [_vm._v(_vm._s(_vm.title))]},{"close":_vm.close,"data":_vm.data})],2),_vm._v(" "),_c('button',{staticClass:"ae-popup__close ae-button",attrs:{"type":"button","aria-label":_vm.closeLabel},on:{"click":function($event){$event.stopPropagation();return _vm.close.apply(null, arguments)}}},[_c('ae-icon',{staticClass:"fa fa-times"})],1)]):_vm._e(),_vm._v(" "),_c('div',{staticClass:"ae-popup__body"},[_vm._t("default",null,{"close":_vm.close,"position":_vm.position,"data":_vm.data,"baseWidth":_vm.baseWidth}),_vm._v(" "),(_vm.visible)?_vm._t("popup",null,{"close":_vm.close,"position":_vm.position,"data":_vm.data,"baseWidth":_vm.baseWidth}):_vm._e()],2)])],2)},
  staticRenderFns: [],
  /* tslint:enable */
  
    data: () => ({
      visible: false,
      supportSensitive: false,
      data: null,
      style: {},
      baseWidth: 0,
      mobilePresentation: false,
    }),
    props: {
      target: {
      },
      placement: {
        type: String,
        default: 'auto'
      },
      disabled: {
        type: Boolean,
        default: false
      },
      trigger: {
        type: String,
        default: 'manual' // Or 'mouseenter focus click' or 'mouseenter'
      },
      delay: {
        type: Array,
        default: () => [100, 10]
      },
      hideOnClick: {
        default: 'toggle' // Or 'true' or 'false'
      },
      touch: {
        default: undefined
      },
      onClose: {
        type: Function,
        default: null
      },
      closeDelay: {
        type: Number,
        default: 0
      },
      interactiveBorder: {
        type: Number,
        default: 50,
      },
      clickable: {
        type: Boolean,
        default: true,
      },
      maxWidth: {
        type: [String, Number],
        default: 350,
      },
      arrow: {
        default: true,
      },
      theme: {
        type: String,
        default: 'ae-tippy-theme',
      },
      exclusive: {
        type: [Boolean, String],
        default: false,
      },
      kind: {
        type: String,
        default: 'popover',
      },
      mobile: {
        type: String,
        default: 'auto',
      },
      title: {
        type: String,
        default: '',
      },
      closeLabel: {
        type: String,
        default: 'Schließen',
      },
    },
    computed: {
      isOpen() {
        return this.visible
      },
      normalizedKind() {
        return String(this.kind || 'popover').toLowerCase()
      },
      normalizedMobile() {
        return String(this.mobile || 'auto').toLowerCase()
      },
      popupRole() {
        if (this.normalizedKind === 'tooltip') {
          return 'tooltip'
        }

        return this.mobilePresentation ? 'dialog' : null
      },
      popupClasses() {
        return {
          'ae-sensitive': this.supportSensitive,
          'ae-popup--mobile': this.mobilePresentation,
          'ae-popup--mobile-fullscreen': this.mobilePresentation && this.normalizedMobile === 'fullscreen',
          'ae-popup--tooltip': this.normalizedKind === 'tooltip',
        }
      },
      triggerList() {
        return String(this.trigger || MANUAL_TRIGGER).split(/\s+/).filter(Boolean)
      },
      exclusiveGroup() {
        if (this.exclusive === true) {
          return DEFAULT_EXCLUSIVE_GROUP
        }

        if (this.exclusive) {
          return String(this.exclusive)
        }

        return null
      },
    },
    methods: {
      toggle(event: Event, data: any) {
        if (this.visible) {
          this.close()
        } else {
          this.show(event, data)
        }
      },
      close() {
        if (this.closeDelay) {
          this._closeTimeout = setTimeout(() => {
              this.closeImmediately()
          }, this.closeDelay)
        } else {
          this.closeImmediately()
        }
      },
      open(...args) {
        console.warn('deprecated: Use show instead')
        this.show(...args)
      },
      hide() {
        this.close()
      },
      show(event: Event|any, data: any) {
        if (this.disabled) {
          return
        }

        if (!event) {
          console.error('Provide a target to AePopup.show')
          return
        }

        const target = hasPointCoordinates(event)
          ? createPointTarget(event, this.target)
          : this.target ?? event.target

        this.data = data
        this._show(target)
      },
      showAtPosition(event: MouseEvent = null, data: any) {
        console.warn('deprecated: use show instead of showAtPosition')

        this.show(event, data)
      },
      cancelClose() {
        if (this._closeTimeout) {
          clearTimeout(this._closeTimeout)
          this._closeTimeout = null
        }
      },
      closeOnBlur(event: FocusEvent) {
        const relatedTarget = event && event.relatedTarget as HTMLElement

        if (relatedTarget && this.$refs.popup && this.$refs.popup.contains(relatedTarget)) {
          return
        }

        this.close()
      },
      popupMouseEnter() {
        if (!this.clickable) {
          return
        }

        this.cancelClose()
        this._clearTriggerTimeouts?.()
      },
      popupMouseLeave() {
        if (!this.clickable || !this.triggerList.includes('mouseenter')) {
          return
        }

        this._hideWithDelay?.()
      },
      position() {
        if (this.mobilePresentation) {
          return
        }

        if (this._popper) {
          this._popper.update()
        }
      },
    },
    created() {
      this._closeTimeout = null
      this._showTimeout = null
      this._hideTimeout = null
      this._popper = null
      this._target = null
      this._targetListeners = []
      this._outsideClickListener = null
      this._originalParent = null
      this._originalNextSibling = null
      this._overlayHost = null
      this._positionFrame = null
      this._schedulePosition = () => {
        if (this._positionFrame) {
          return
        }

        this._positionFrame = requestAnimationFrame(() => {
          this._positionFrame = null

          if (this.visible) {
            this.position()
          }
        })
      }
      this._closeExclusivePopups = (target: HTMLElement|string|any) => {
        const group = this.exclusiveGroup
        if (!group) {
          return
        }

        const activePopups = activeExclusivePopups.get(group) || []
        const remainingPopups = []

        for (const popup of activePopups) {
          if (!popup || popup === this || !popup.visible) {
            continue
          }

          if (targetIsInsidePopup(popup, target)) {
            remainingPopups.push(popup)
            continue
          }

          popup.closeImmediately()
        }

        remainingPopups.push(this)
        activeExclusivePopups.set(group, remainingPopups)
      }

      this._unregisterExclusivePopup = () => {
        const group = this.exclusiveGroup
        if (!group) {
          return
        }

        const activePopups = activeExclusivePopups.get(group)
        if (!activePopups) {
          return
        }

        const remainingPopups = activePopups.filter((popup) => popup !== this)
        if (remainingPopups.length) {
          activeExclusivePopups.set(group, remainingPopups)
        } else {
          activeExclusivePopups.delete(group)
        }
      }

      this._clearTriggerTimeouts = () => {
        if (this._showTimeout) {
          clearTimeout(this._showTimeout)
          this._showTimeout = null
        }

        if (this._hideTimeout) {
          clearTimeout(this._hideTimeout)
          this._hideTimeout = null
        }
      }

      this._bindTarget = (target: HTMLElement|string|any) => {
        target = normalizeTarget(target)

        if (!target || target === this._target) {
          return target
        }

        this.unregister()
        this._target = target
        this._bindTriggerListeners()

        return target
      }

      this._bindTriggerListeners = () => {
        if (!this._target || this.triggerList.includes(MANUAL_TRIGGER)) {
          return
        }

        if (typeof this._target.addEventListener !== 'function') {
          return
        }

        for (const eventName of TARGET_EVENT_NAMES) {
          if (!this.triggerList.includes(eventName)) {
            continue
          }

          const listener = (event) => this._onTargetEvent(event)
          this._target.addEventListener(eventName, listener)
          this._targetListeners.push({ eventName, listener })
        }
      }

      this._unbindTriggerListeners = () => {
        for (const { eventName, listener } of this._targetListeners) {
          this._target?.removeEventListener(eventName, listener)
        }

        this._targetListeners = []
      }

      this._onTargetEvent = (event: Event) => {
        if (this.disabled) {
          return
        }

        if (event.type === 'click') {
          if ((this.hideOnClick === 'toggle' || this.hideOnClick === true || this.hideOnClick === 'true') && this.visible) {
            this.close()
            return
          }

          this.data = null
          this._showWithDelay(this._target || event.currentTarget)
          return
        }

        if (event.type === 'mouseenter' || event.type === 'focus') {
          this.data = null
          this._showWithDelay(this._target || event.currentTarget)
          return
        }

        if (event.type === 'mouseleave' || event.type === 'blur') {
          this._hideWithDelay()
        }
      }

      this._showWithDelay = (target) => {
        this._clearTriggerTimeouts()

        const delay = resolveDelay(this.delay, 0)
        if (delay > 0) {
          this._showTimeout = setTimeout(() => {
            this._showTimeout = null
            this._show(target)
          }, delay)
          return
        }

        this._show(target)
      }

      this._hideWithDelay = () => {
        this._clearTriggerTimeouts()

        const delay = resolveDelay(this.delay, 1)
        if (delay > 0) {
          this._hideTimeout = setTimeout(() => {
            this._hideTimeout = null
            this.close()
          }, delay)
          return
        }

        this.close()
      }

      this._appendPopupToOverlayHost = (target?: HTMLElement|any) => {
        const popup = this.$refs.popup

        if (!popup) {
          return
        }

        if (!this._originalParent) {
          this._originalParent = popup.parentNode
          this._originalNextSibling = popup.nextSibling
        }

        // Preserve privacy before moving content outside its original DOM boundary.
        const supportBoundary = '.ae-sensitive,[data-ae-sensitive],.rr-block'
        const supportTarget = target?.contextElement || target
        if (popup.closest(supportBoundary) || supportTarget?.closest?.(supportBoundary)) {
          this.supportSensitive = true
          popup.classList.add('ae-sensitive')
        }

        const ownerDocument = popup.ownerDocument || target?.ownerDocument || target?.contextElement?.ownerDocument || document
        const targetElement = isNode(target) ? target : target?.contextElement
        // Layered overlays: popups open above the stack of the layer their target lives in (developer UI stays legacy).
        const layeredHost = targetElement?.closest?.('.ae-developer') ? null : findLayeredPopupHost(targetElement, ownerDocument)
        this._overlayHost = layeredHost || getNearestTargetOverlayHost(target) || getOrCreateRootOverlayHost(ownerDocument) || ownerDocument.body
        this._overlayHost.appendChild(popup)

        if (layeredHost) {
          setOverlayOwner(popup, this._originalParent)
        }
        this.style = Object.assign({}, this.style, {
          pointerEvents: 'auto',
          zIndex: 'auto',
        })
      }

      this._restorePopupPosition = () => {
        const popup = this.$refs.popup

        if (!popup || !this._originalParent || popup.parentNode === this._originalParent) {
          return
        }

        setOverlayOwner(popup, null)

        if (this._originalNextSibling && this._originalNextSibling.parentNode === this._originalParent) {
          this._originalParent.insertBefore(popup, this._originalNextSibling)
          return
        }

        this._originalParent.appendChild(popup)
      }

      this._bindOutsideClick = () => {
        if (this._outsideClickListener || !this.$refs.popup) {
          return
        }

        const ownerDocument = this.$refs.popup.ownerDocument || document
        this._outsideClickListener = (event) => {
          if (!this.visible) {
            return
          }

          if (this.$refs.popup?.contains(event.target) || elementContains(this._target, event.target)) {
            return
          }

          // This prevents closing the parent popup when nesting popups (because they are not actually nested, but siblings)
          if (findAncestorWithClass(event.target, 'ae-popup')) {
            return
          }

          if (this.hideOnClick !== false) {
            this.close()
          }
        }

        ownerDocument.addEventListener('mousedown', this._outsideClickListener, true)
        ownerDocument.addEventListener('touchstart', this._outsideClickListener, true)
      }

      this._unbindOutsideClick = () => {
        if (!this._outsideClickListener || !this.$refs.popup) {
          return
        }

        const ownerDocument = this.$refs.popup.ownerDocument || document
        ownerDocument.removeEventListener('mousedown', this._outsideClickListener, true)
        ownerDocument.removeEventListener('touchstart', this._outsideClickListener, true)
        this._outsideClickListener = null
      }

      this._create = (target: HTMLElement|string|any) => {
        const boundTarget = this._bindTarget(target) as HTMLElement|any

        if (this._popper || !boundTarget || !this.$refs.popup) {
          return
        }

        this.baseWidth = boundTarget.getBoundingClientRect ? boundTarget.getBoundingClientRect().width : boundTarget.offsetWidth || 0
        this._appendPopupToOverlayHost(boundTarget)

        if (this.mobilePresentation) {
          this.style = {
            pointerEvents: 'auto',
            zIndex: 'auto',
          }
          return
        }

        if (this.maxWidth === '') {
          this.style = Object.assign({}, this.style, { maxWidth: null, maxHeight: null, overflow: null })
        } else {
          this.style = Object.assign({}, this.style, {
            boxSizing: 'border-box',
            maxWidth: normalizePopupMaxWidth(this.maxWidth),
            maxHeight: `calc(100vh - ${POPPER_VIEWPORT_INSET})`,
            overflow: 'auto',
          })
        }

        this._popper = createPopper(boundTarget, this.$refs.popup, {
          placement: this.placement,
          strategy: 'fixed',
          modifiers: [
            {
              name: 'computeStyles',
              options: {
                adaptive: false,
                gpuAcceleration: false,
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'clippingParents',
                altBoundary: true,
                rootBoundary: 'viewport',
                padding: POPPER_VIEWPORT_PADDING,
              },
            },
            {
              name: 'flip',
              options: {
                boundary: 'clippingParents',
                altBoundary: true,
                rootBoundary: 'viewport',
                padding: POPPER_VIEWPORT_PADDING,
                fallbackPlacements: ['bottom-start', 'top-start', 'left-start', 'bottom', 'top', 'left'],
              },
            },
          ],
        })

        if (!this._popper) {
          console.error('Failed to create Popper instance')
          return
        }
      }

      this._show = async (target) => {
        target = this._bindTarget(target)

        if (!target) {
          return
        }

        this.cancelClose()
        this._clearTriggerTimeouts()
        this._closeExclusivePopups(target)
        this.mobilePresentation = this._shouldUseMobilePresentation(target)
        this.visible = true
        this._create(target)
        this._bindOutsideClick()

        this.$nextTick(() => {
          this.position()
          this.$emit('show')
        })
      }

      this.closeImmediately = () => {
        this.cancelClose()
        this._clearTriggerTimeouts()

        if (!this.visible && !this._popper) {
          return
        }

        this.visible = false
        this.mobilePresentation = false
        this._baseNode = null
        this._unregisterExclusivePopup()
        this._unbindOutsideClick()

        // Restore the popup before Vue patches its original parent. While the
        // popup is open its DOM is teleported to the overlay host, but Vue still
        // owns it in the source tree. Deferring restoration until nextTick lets
        // a simultaneous parent update reconcile against the wrong DOM parent.
        this.destroyPopper()

        this.$nextTick(() => {
          this.$emit('hide')
          this.$emit('close')
        })
      }

      this.destroyPopper = () => {
        if (this._popper) {
          this._popper.destroy()
          this._popper = null
        }

        this._restorePopupPosition()
      }

      this.unregister = () => {
        this._unbindOutsideClick()
        this._unbindTriggerListeners()
        this.destroyPopper()
        this._target = null
      }

      this._shouldUseMobilePresentation = (target?: HTMLElement|any) => {
        const mobileMode = this.normalizedMobile

        if (mobileMode === 'none' || mobileMode === 'popover' || this.normalizedKind === 'tooltip') {
          return false
        }

        if (mobileMode === 'always' || mobileMode === 'dialog' || mobileMode === 'fullscreen') {
          return this._isMobileViewport(target)
        }

        return this._isMobileViewport(target)
      }

      this._isMobileViewport = (target?: HTMLElement|any) => {
        const ownerWindow = target?.ownerDocument?.defaultView || (typeof window !== 'undefined' ? window : null)

        if (!ownerWindow) {
          return false
        }

        return ownerWindow.matchMedia?.('(max-width: 640px)').matches
          || ownerWindow.innerWidth <= 640
      }
    },
    mounted() {
      loadStyle(STYLE)

      if (this.target) {
        this._bindTarget(this.target)
      }

      if (typeof ResizeObserver === 'function') {
        this._resizeObserver = new ResizeObserver(() => {
          this._schedulePosition()
        })
        this._resizeObserver.observe(this.$refs.popup)
      } else {
        this._resizeObserver = null
      }
    },
    watch: {
      target: {
        handler(newTarget) {
          if (newTarget) {
            this.unregister()
            this._bindTarget(newTarget)
          }
        }
      },
      disabled() {
        if (this.disabled) {
          this.closeImmediately()
        }
      }
    },
    beforeDestroy() {
      this._resizeObserver?.disconnect()
      if (this._positionFrame) {
        cancelAnimationFrame(this._positionFrame)
        this._positionFrame = null
      }
      this.closeImmediately()
      this.unregister()
    }
  }
